import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { customerService } from '../api';
import { Button } from './Button';
import { Input } from './Input';
import { ThemedText } from './ThemedText';
import { X } from 'lucide-react';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const CustomerFormModal = ({ isOpen, onClose, onSuccess, editCustomer }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => {
    if (editCustomer) {
      setFormData({
        firstName: editCustomer.firstName || '',
        lastName: editCustomer.lastName || '',
        email: editCustomer.email || '',
        phone: editCustomer.phone || '',
        company: editCustomer.company || '',
      });
    } else {
      resetForm();
    }
  }, [editCustomer, isOpen]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      toast.error('First Name, Last Name, and Email are required');
      return;
    }

    try {
      if (editCustomer) {
        const updated = await customerService.updateCustomer(editCustomer.id, formData);
        toast.success('Customer updated successfully');
        onSuccess && onSuccess(updated);
      } else {
        const created = await customerService.createCustomer(formData);
        toast.success('Customer created successfully');
        onSuccess && onSuccess(created);
      }
      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to save customer:', error);
      toast.error(error.response?.data?.message || `Failed to ${editCustomer ? 'update' : 'create'} customer`);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal theme={theme} isOpen={isOpen}>
      <ModalContent theme={theme}>
        <ModalHeader theme={theme}>
          <ThemedText type="subtitle">{editCustomer ? 'Edit Customer' : 'New Customer'}</ThemedText>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            <X size={16} />
          </Button>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>First Name *</Label>
              <Input
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="Enter first name"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Last Name *</Label>
              <Input
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Enter last name"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Email *</Label>
              <Input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="customer@example.com"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Enter company name"
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter theme={theme}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editCustomer ? 'Update Customer' : 'Create Customer'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CustomerFormModal;
export { CustomerFormModal };
